<template>
  <div class="p-6 bg-white/60 rounded-xl min-h-screen">
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold text-gray-800 inline-block transition-all duration-500">
        <span
            class="bg-clip-text text-transparent bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 hover:animate-pulse">
          تایین وضعیت یوان
        </span>
      </h1>
      <div class="flex gap-3 mr-3">
        <button
            @click="refreshProducts"
            class="p-2 bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 hover:bg-gray-50 transform hover:scale-105"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24"
               stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
          </svg>
        </button>
      </div>
    </div>
    <div class="bg-white bg-opacity-70 backdrop-filter backdrop-blur-lg rounded-xl p-3 mb-6 shadow-sm border border-gray-100">
      <div class="flex flex-wrap items-center gap-2">
        <!-- Search -->
        <div class="relative w-32">
          <input
              v-model="filters.name_search"
              placeholder="نام"
              class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-full text-right"
              @keyup.enter="applyFilters"
          >
        </div>
        <div class="relative w-32">
          <input
              v-model="filters.sku"
              placeholder="SKU"
              class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-full text-right"
              @keyup.enter="applyFilters"
          >
        </div>
        <div class="relative w-32">
          <input
              v-model="filters.id_in"
              placeholder="شناسه"
              class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-full text-right"
              @keyup.enter="applyFilters"
          >
        </div>
        <!-- Brand -->
        <select
            @change="applyFilters"
            v-model="filters.brand_in"
            class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-32 text-right"
        >
          <option value="">همه برندها</option>
          <option
              v-for="brand in brands"
              :key="brand.id"
              :value="brand.id"
          >
            {{ brand.name }}
          </option>
        </select>
        <select
            @change="applyFilters"
            v-model="filters.status_in"
            class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-32 text-right"
        >
          <option value="">همه وضعیت ها</option>
          <option value="pending">در انتظار تایید</option>
          <option value="draft">پیش نویس</option>
          <option value="publish">منتشر شده</option>
        </select>

        <!-- Action Buttons -->
        <div class="flex gap-1">
          <button
              @click="resetFilters"
              class="p-1.5 bg-gradient-to-r from-gray-500 to-blue-600 text-white rounded-full shadow-sm hover:shadow-md transition-all duration-300 transform hover:scale-105"
              title="حذف فیلترها"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>

          <button
              @click="applyFilters"
              class="p-1.5 bg-gradient-to-r from-red-500 to-blue-600 text-white rounded-full shadow-sm hover:shadow-md transition-all duration-300 transform hover:scale-105"
              title="اعمال فیلتر"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div
        class="bg-white bg-opacity-70 backdrop-filter backdrop-blur-lg rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead class="bg-gray-50 bg-opacity-70">
          <tr>
            <th class="p-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">شناسه</th>
            <th class="p-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
            <th class="p-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">محصول</th>
            <th class="p-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">نوع</th>
            <th class="p-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">موجودی</th>
            <th class="p-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">وضعیت</th>
            <th class="p-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">قیمت فروش در سایت</th>
            <th class="p-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">قیمت پایه</th>
            <th class="p-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">حاشیه سود</th>
            <th class="p-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
            <th class="p-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
          </tr>
          </thead>
          <tbody>
          <tr v-if="loading">
            <td colspan="6" class="p-4 text-center">
              <div class="flex justify-center items-center gap-2">
                <svg class="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none"
                     viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                در حال بارگذاری...
              </div>
            </td>
          </tr>
          <tr v-else-if="products.length === 0">
            <td colspan="6" class="p-4 text-center text-gray-500">
              محصولی یافت نشد
            </td>
          </tr>
          <tr
              v-for="product in products"
              :key="product.id"
              class="border-t border-gray-100 hover:bg-gray-50 transition-colors duration-200"
          >
            <td class="p-3 font-medium text-[9px] text-gray-900 w-min">{{ product.id  || ' بدون شناسه'}}</td>
            <td class="p-3 font-medium text-[9px] text-gray-900 w-min">{{ product.sixteen_digit_code  || ' بدون کد'}}</td>
            <td class="p-3">
              <div class="flex items-center gap-3">
                <div class="relative" v-if="product.image">
                  <img
                      :src="img_source(product.image)"
                      class="w-10 h-10 rounded-lg object-cover border border-gray-200 shadow-sm"
                  >
                </div>
                <div>
                  <div class="text-[9px] font-medium text-gray-900">{{ product.name }}</div>
                  <div class="text-[9px] text-gray-500 text-xs mt-1">{{ product.sixteen_digit_code }}</div>
                  <div class="text-[9px] text-gray-500 text-xs mt-1 flex items-center gap-1"
                       v-if="product.product_type == 'variation'">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24"
                         stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/>
                    </svg>
                    {{ product.variation_of_name }}
                  </div>
                </div>
              </div>
            </td>
            <td class="w-min">
                <span
                    class="px-2.5 py-1 rounded-full text-[8px] font-medium"
                    :class="{
                    'bg-green-100 text-green-800': product.product_type === 'simple',
                    'bg-blue-100 text-blue-800': product.product_type === 'variable',
                    'bg-red-100 text-red-800': product.product_type === 'variation'
                  }"
                >
                  {{ getTypeLabel(product.product_type) }}
                </span>
            </td>
            <td class="p-3 text-[9px] w-min">
              <div class="flex items-center gap-1"
                   v-if="product.calculate_current_inventory == 0">
                <span class="bg-red-300 text-red-600 px-1.5 py-0.5 rounded-full text-[7px] font-medium">
                   اتمام موجودی
                </span>
              </div>
              <div class="flex items-center gap-1 text-[9px]"
                   v-if="product.calculate_current_inventory > 0">
                {{ product.calculate_current_inventory | toMoney }}
                <span class="text-xs text-gray-500 text-[7px]">عدد</span>
              </div>
            </td>
            <td class="w-min" >
              <span
                  class="px-1.5 py-0.5 rounded-full text-[8px] font-medium"
                  :class="{
                 'bg-green-100 text-green-800': product.status === 'publish',
                 'bg-yellow-100 text-yellow-800': product.status === 'pending',
                 'bg-gray-100 text-gray-800': product.status === 'draft',
                 'bg-red-100 text-red-800': product.status === 'archived'
               }"
              >
               {{ getStatusLabel(product.status) }}
                </span>
            </td>
            <td class="w-40" >
              <div class="text-[9px] font-medium text-gray-900">{{ product.price | toMoney }} تومان</div>
              <div v-if="product.regular_price > product.price" class="text-[8px] text-green-600 line-through mt-1">
                {{ product.regular_price | toMoney }}
                تومان
              </div>
            </td>

            <td class="w-min">
              <div class="relative rounded-md shadow-sm">
                <input
                    v-model.number="product.base_price"
                    type="number"
                    id="base_price"
                    min="0"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none ring-1 ring-blue-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 p-2 m-2"
                >
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span class="text-gray-500 text-[8px]">یوان</span>
                </div>
              </div>
            </td>
            <td class="w-min pr-2">
              <div class="relative rounded-md shadow-sm">
                <input
                    v-model.number="product.profit_margin"
                    type="number"
                    id="profit_margin"
                    min="0"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none ring-1 ring-blue-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 p-2 m-2"
                >
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span class="text-gray-500 text-[8px]">درصد</span>
                </div>
              </div>
            </td>
            <td>
              <button @click="onAction(product)" class="px-5 py-2 rounded-lg text-white bg-green-800 hover:bg-green-900 transition mr-4">
                ثبت
              </button>

            </td>

          </tr>
          </tbody>
        </table>
      </div>
      <!-- Pagination -->
      <div class="p-4 border-t border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4">
        <div class="text-sm text-gray-600">
          نمایش {{ (currentPage - 1) * itemsPerPage + 1 }} تا
          {{ Math.min(currentPage * itemsPerPage, totalProducts) }} از
          {{ totalProducts }} محصول
        </div>
        <div class="flex gap-1">
          <button
              @click="prevPage"
              :disabled="currentPage === 1"
              class="p-2 border border-gray-200 rounded-lg bg-white bg-opacity-50 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              :class="{ 'opacity-50 cursor-not-allowed': currentPage === 1 }"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                 stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </button>
          <button
              v-for="page in visiblePages"
              :key="page"
              @click="goToPage(page)"
              class="p-2 text-center border border-gray-200 rounded-full transition-colors duration-200"
              :class="{
              'bg-gradient-to-r from-blue-500/60 to-blue-600/50 text-white shadow-md': page === currentPage,
              'bg-white bg-opacity-50 hover:bg-gray-50': page !== currentPage,
              'cursor-default': page === '...'
            }"
              :disabled="page === '...'"
          >
            {{ page }}
          </button>
          <button
              @click="nextPage"
              :disabled="currentPage * itemsPerPage >= totalProducts"
              class="p-2 border border-gray-200 rounded-lg bg-white bg-opacity-50 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              :class="{ 'opacity-50 cursor-not-allowed': currentPage * itemsPerPage >= totalProducts }"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                 stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
    <transition name="slide-fade">
      <div v-if="showCalculate"
           class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">

        <div class="bg-gray-900 text-gray-100 rounded-xl shadow-lg border border-gray-700 w-[90%] max-w-md p-6">
          <h3 class="text-lg font-semibold mb-3">ثبت محصول</h3>

          <div v-if="selectedProduct" class="text-sm text-gray-300 space-y-1 mb-4">
            <div>💵 قیمت پایه: {{ selectedProduct.base_price }}</div>
            <div> درصد سود: {{ selectedProduct.profit_margin }}٪</div>
            <div>📈 ضریب یوان: {{ selectedProduct.rate }}</div>
            <div>
              ✅ قیمت نهایی: {{selectedProduct.final_price | toMoney}}
              تومان
            </div>
          </div>

          <p class="text-sm text-gray-400 mb-6">آیا می‌خواهید این تغییرات اعمال شوند؟</p>

          <div class="flex justify-end gap-3">
            <button @click="onCancel"
                    class="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600">
              انصراف
            </button>
            <button @click="setBasePrice(selectedProduct)"
                    class="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-500">
              تایید
            </button>
          </div>
        </div>
      </div>
    </transition>

    <transition name="slide-fade">
      <div v-if="showNotification"
           class="fixed bottom-4 right-4 z-50 flex items-center p-4 w-[90%] max-w-xs text-gray-100 bg-gray-900 rounded-lg shadow-lg border border-gray-700">

        <div
            class="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-lg text-green-400 bg-gray-800">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clip-rule="evenodd"/>
          </svg>
        </div>

        <div class="mr-3 text-sm font-normal pr-2">ثبت شد</div>

        <button @click="showNotification = false"
                class="mr-auto text-gray-400 hover:text-white rounded-lg p-1.5 hover:bg-gray-800">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd"/>
          </svg>
        </button>
      </div>
    </transition>

  </div>
</template>

<script>
import ProductQuickView from "@/modules/product_admin/QuickViewModal";
import AdminProductForm from "@/modules/product_admin/AdminProductForm";
export default {
  name: 'AdminProductList',
  components: {AdminProductForm, ProductQuickView},
  data() {
    return {
      showQuickView: false,
      showNotification: false,
      showQuickEdit: false,
      showCalculate: false,
      selectedProduct: null,
      productResponse: null,
      loading: true,
      products: [],
      totalProducts: 0,
      currentPage: 1,
      itemsPerPage: 20,
      filters: {
        id_in: '',
        name_search: '',
        sku: '',
        search_value: '',
        min_price: '',
        max_price: '',
        min_inventory: '',
        max_inventory: '',
        category_tree: '',
        brand_in: '',
        status_in: '',
        currency_in: '',
        supplier: null
      },
      categories: [],
      brands: [],
      currencies: []
    }
  },
  computed: {
    visiblePages() {
      const totalPages = Math.ceil(this.totalProducts / this.itemsPerPage)
      const range = 1
      const pages = []

      pages.push(1)

      if (this.currentPage - range > 2) {
        pages.push('...')
      }

      for (let i = Math.max(2, this.currentPage - range); i <= Math.min(totalPages - 1, this.currentPage + range); i++) {
        pages.push(i)
      }

      if (this.currentPage + range < totalPages - 1) {
        pages.push('...')
      }

      if (totalPages > 1) {
        pages.push(totalPages)
      }

      return pages
    },
    publishedProducts() {
      return this.products.filter(p => p.status === 'publish').length
    },
    totalInventory() {
      return this.products.reduce((sum, product) => sum + product.calculate_current_inventory, 0)
    },
    averagePrice() {
      if (this.products.length === 0) return 0
      const sum = this.products.reduce((sum, product) => sum + product.price, 0)
      return Math.round(sum / this.products.length)
    }
  },
  methods: {
    showProductQuickView(product) {
      this.selectedProduct = product
      this.showQuickView = true
    },
    showProductQuickEdit(product) {
      this.selectedProductId = product.id
      this.showQuickEdit = true
    },
    handleEditProduct(product) {
      this.$router.push(`/panel/adminProductForm/${product.id}`)
    },
    onAction(product){
      this.calculatePrice(product)
      this.showCalculate = true
    },
    onCancel() {
      this.selectedProduct = null
      this.showCalculate = false

    },
    fetchProducts() {
      this.loading = true

      const params = {
        limit: this.itemsPerPage,
        offset: (this.currentPage - 1) * this.itemsPerPage,
        ...this.filters
      }

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '') {
          delete params[key]
        }
      })

      this.request({
        url: this.endpoint('product/yoanToFix'),
        method: 'get',
        params,
        success: (data) => {
          this.products = data.results
          this.totalProducts = data.count
          this.loading = false
          this.showCalculate = false
        },
        error: () => {
          this.loading = false
          this.showCalculate = false
        }
      })
    },

    fetchCategories() {
      this.request({
        url: this.endpoint('api/rootCategories'),
        method: 'get',
        success: (data) => {
          this.categories = data
        }
      })
    },
    showNotificationMessage() {
      this.showNotification = true;
      setTimeout(() => {
        this.showNotification = false;
      }, 5000);
    },
    calculatePrice(product){
      let final_price = product.base_price * 15500 * (1 + product.profit_margin / 100);
      final_price = Math.ceil(final_price / 1000) * 1000;

      this.selectedProduct = {
        id: product.id,
        rate: 15500,
        base_price: product.base_price,
        profit_margin: product.profit_margin,
        final_price: final_price,
      }

    },

    setBasePrice(product) {
      if (!product.base_price || !product.profit_margin) {
        window.alert(`اطلاعات  کامل نیست`)
        return
      }
      this.isLoading = true
      this.request({
        url: this.endpoint(`product/setBasePrice`),
        method: 'post',
        data: {
          id: product.id,
          base_price: product.base_price,
          profit_margin: product.profit_margin,

        },
        success: (data) => {
          this.isLoading = false
          this.showNotificationMessage()
          this.fetchProducts()
          this.selectedProduct = product
        },
        error: (error) => {
          window.alert(error)
          this.isLoading = false
        }
      })
    },

    fetchBrands() {
      this.request({
        url: this.endpoint('api/brands'),
        method: 'get',
        success: (data) => {
          this.brands = data
        }
      })
    },
    fetchCurrency() {
      this.request({
        url: this.endpoint('main/currency'),
        method: 'get',
        success: (data) => {
          this.currencies = data
        }
      })
    },

    applyFilters() {
      this.currentPage = 1
      this.fetchProducts()
    },

    resetFilters() {
      this.filters = {
        search_value: '',
        id_in: '',
        sku: '',
        name_search: '',
        min_price: '',
        max_price: '',
        min_inventory: '',
        max_inventory: '',
        category_tree: '',
        brand_in: '',
        currency_in: ''
      }
      this.applyFilters()
    },

    refreshProducts() {
      this.fetchProducts()
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--
        this.fetchProducts()
      }
    },

    nextPage() {
      if (this.currentPage * this.itemsPerPage < this.totalProducts) {
        this.currentPage++
        this.fetchProducts()
      }
    },

    goToPage(page) {
      if (page !== '...') {
        this.currentPage = page
        this.fetchProducts()
      }
    },

    getStatusLabel(status) {
      const statusMap = {
        'draft': 'پیش نویس',
        'pending': 'در انتظار تایید',
        'publish': 'منتشر شده',
      }
      if (!status) return ' '
      return statusMap[status] || status
    },
    getTypeLabel(type) {
      const typeMap = {
        'simple': 'ساده',
        'variable': 'دارای متغیر',
        'variation': 'متغیر',
      }
      if (!type) return ' '
      return typeMap[type] || type
    },

  },
  mounted() {
    for (let param in this.$route.query) {
      if(param == 'brand') {
        this.filters.brand_in = this.$route.query['brand']
      }
      if(param == 'currency') {
        this.filters.currency_in = this.$route.query['currency']

      }
      if(param == 'supplier') {
        this.filters.currency_in = this.$route.query['supplier']

      }
    }

    this.fetchProducts()
    this.fetchBrands()
  }
}
</script>

<style>
/* RTL support */
body {
  direction: rtl;
  text-align: right;
}

/* Glass morphism effect */
.glass {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.18);
}

/* Neumorphism effect */
.neumorphism {
  border-radius: 16px;
  background: #f0f0f0;
  box-shadow: 8px 8px 16px #d9d9d9,
  -8px -8px 16px #ffffff;
}

/* Smooth transitions */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}
.truncate-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100px; /* or whatever width you need */
}

</style>